import type { GridPoint } from '../../core/types';

/**
 * Which building (if any) occupies each tile. Structured data — never derived
 * from sprites. 0 = free; otherwise the building's id.
 */
export class OccupancyGrid {
  private readonly cells: Int32Array;

  constructor(
    readonly width: number,
    readonly height: number,
  ) {
    this.cells = new Int32Array(width * height);
  }

  inBounds(x: number, y: number): boolean {
    return Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  /** Building id at a tile, or 0 when free or off the map. */
  at(x: number, y: number): number {
    return this.inBounds(x, y) ? this.cells[y * this.width + x]! : 0;
  }

  isFree(x: number, y: number): boolean {
    return this.inBounds(x, y) && this.at(x, y) === 0;
  }

  /** Mark tiles as occupied. Throws if any tile is off-map or already taken (callers must validate first). */
  occupy(tiles: readonly GridPoint[], id: number): void {
    if (!(id > 0)) throw new Error(`Invalid building id ${id}`);
    for (const t of tiles) {
      if (!this.inBounds(t.x, t.y)) throw new RangeError(`Tile ${t.x},${t.y} is off the map`);
      const cur = this.at(t.x, t.y);
      if (cur !== 0) throw new Error(`Tile ${t.x},${t.y} already occupied by #${cur}`);
    }
    for (const t of tiles) this.cells[t.y * this.width + t.x] = id;
  }

  /** Free every tile held by a building. Returns how many tiles were freed. */
  release(id: number): number {
    let n = 0;
    for (let i = 0; i < this.cells.length; i++) {
      if (this.cells[i] === id) {
        this.cells[i] = 0;
        n++;
      }
    }
    return n;
  }

  occupiedCount(): number {
    let n = 0;
    for (const c of this.cells) if (c !== 0) n++;
    return n;
  }
}
